import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { XIcon } from "lucide-react";
import { MenuButton, MenuButtonItem } from "@repo/ui";
import type { FieldValues } from "react-hook-form";

import { FieldProps } from "./types";
import { useGoogleFonts } from "hooks/api/useGoogleFonts";
import { GoogleFont } from "hooks/api/useGoogleFonts";
import type { CompanyFont } from "hooks/api/useCompanyFonts";

interface FontSelectorFieldProps<T extends FieldValues = FieldValues> extends FieldProps<T> {
  /** The brand's uploaded fonts, listed above the Google set (VRG-348). */
  customFonts?: CompanyFont[];
  /** Remove an uploaded font. Omit to hide the per-row remove control. */
  onRemoveCustomFont?: (font: CompanyFont) => void;
}

const FontSelectorFieldImpl = ({
  fieldName,
  label = "",
  setValue,
  watch,
  options,
  defaultValue,
  customFonts,
  onRemoveCustomFont,
}: FontSelectorFieldProps) => {
  const [selected, setSelected] = useState<MenuButtonItem | null>(null);

  const { data: googleFonts, isLoading, error } = useGoogleFonts();

  // Use Google Fonts if available, otherwise fallback to passed options
  const availableFonts = useMemo(() => {
    const fonts =
      googleFonts && googleFonts.data.length > 0 ? googleFonts.data : options;

    if (!fonts) return [];

    return fonts.filter((font: GoogleFont) => font.name !== "Material Icons");
  }, [googleFonts, options]);

  /**
   * The brand's own fonts first, then Google's.
   *
   * A custom font's `value` and `style.fontFamily` are both the file URL, not a
   * family name — that is the contract `useDynamicFontLoader` sets up. MenuButton
   * loads by URL and renders through `resolveFontFamily`, so the URL never
   * reaches CSS `font-family`.
   */
  const items = useMemo<MenuButtonItem[]>(() => {
    const custom: MenuButtonItem[] = (customFonts ?? []).map((font) => ({
      label: font.family_label,
      value: font.file_url,
      style: { fontFamily: font.file_url },
      group: "Your fonts",
      trailing: onRemoveCustomFont ? (
        // An X, not a trash can: this removes the font from the picker and
        // leaves both the file and its licensing record in place, so nothing is
        // destroyed (§38).
        <Tooltip title="Remove from your fonts" arrow>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveCustomFont(font);
            }}
          >
            <XIcon size={16} strokeWidth={1} />
          </IconButton>
        </Tooltip>
      ) : undefined,
    }));

    const google: MenuButtonItem[] = availableFonts.map(
      (option: { id: string; name: string }) => ({
        label: option.name,
        value: option.name,
        style: { fontFamily: option.name },
        // Only label the Google group when there is something to distinguish it
        // from; a lone "Google fonts" subheader over the whole list is noise.
        group: custom.length ? "Google fonts" : undefined,
      }),
    );

    return [...custom, ...google];
  }, [availableFonts, customFonts, onRemoveCustomFont]);

  const handleSelect = (item: MenuButtonItem) => {
    // The VALUE, not the label. For a Google font the two are the same string;
    // for an uploaded font the value is the URL and the label is the brand's
    // display name, and storing the label would produce a theme naming a font
    // family that does not exist.
    setValue(fieldName as string, item.value as string);
    setSelected(item);
  };

  // Live form value wins; the saved theme is the fallback for first paint.
  // `watch` comes from FieldProps -> UseFormReturn via the `{...methods}` spread;
  // it is NOT redeclared here. Doing so narrowed react-hook-form's own signature
  // and made the interface incompatibly extend FieldProps (TS2430).
  //
  // The picker must show the form's LIVE value, not just the saved theme: the
  // upload modal selects a newly uploaded font with `setValue`, and without this
  // the control kept displaying the previously saved font while the form held the
  // new one — the UI stating one thing and Save about to persist another.
  const liveValue = watch?.(fieldName as string) as string | undefined;
  const currentValue = liveValue || defaultValue;

  useEffect(() => {
    // Match on `value`, which covers both kinds: a Google family name and an
    // uploaded font's URL.
    const match = items.find((item) => item.value === currentValue) ?? null;
    // Compare by VALUE, not object identity, and bail when unchanged.
    // `items` is rebuilt whenever any of its inputs changes identity — which a
    // parent passing an inline `onRemoveCustomFont` does on EVERY render — so an
    // unconditional `setSelected` here hands React a new object each pass and
    // spins until "Maximum update depth exceeded". The parent is also memoized,
    // but this guard is what makes the component safe for any caller.
    setSelected((prev) =>
      prev?.value === (match?.value ?? undefined) ? prev : match,
    );
  }, [currentValue, items]);

  /**
   * Spin only while the font list is genuinely loading.
   *
   * This previously also required `selected` to be set, which made the spinner
   * PERMANENT in two real cases: a company that had never chosen a font (nothing
   * to match, so `selected` stayed null), and — once custom fonts existed — any
   * value that is not a Google family name. In both the control became
   * unreachable, with "Loading fonts..." forever. Whether a value is currently
   * selected is not the same question as whether the options have loaded.
   */
  if (isLoading) {
    return (
      <Box sx={{ minWidth: "100%", maxWidth: "100%" }}>
        {label && (
          <Typography mb="8px" variant="body3" component={"p"}>
            {label}
          </Typography>
        )}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={44}
        >
          <CircularProgress size={20} />
          <Typography ml={1} variant="body3">
            Loading fonts...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: "100%", maxWidth: "100%" }}>
      {label && (
        <Typography mb="8px" variant="body3" component={"p"}>
          {label}
        </Typography>
      )}
      {error && (
        <Typography variant="body3" color="warning.main" mb={1}>
          {error.message} - Using fallback fonts
        </Typography>
      )}
      <MenuButton
        fullWidth
        variant="secondary"
        limit={10}
        search
        // A never-selected field shows a prompt rather than an empty button.
        selected={selected ?? { label: "Select a font", value: "" }}
        onSelect={handleSelect}
        items={items}
        sx={{
          minWidth: "100%",
          width: "100%",
          height: "440px",
        }}
        // Match the surrounding form inputs (grey[50]) rather than the
        // lighter secondary-button surface.
        buttonProps={{
          sx: {
            backgroundColor: "grey.50",
            "&:hover": { backgroundColor: "grey.50" },
          },
        }}
        // The raw value (family name OR url) — MenuButton resolves it for CSS.
        selectedItemFontFamily={(selected?.value as string) || "inherit"}
        isInfiniteScroll={true}
      />
    </Box>
  );
};

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression.
 */
export const FontSelectorField = FontSelectorFieldImpl as <T extends FieldValues = FieldValues>(
  props: FontSelectorFieldProps<T>,
) => React.ReactElement | null
